import { useEffect, useState, type FormEvent } from 'react'
import { Printer } from 'lucide-react'
import {
  LabelPrintingApiError,
  type IsbtPrinter,
  type IsbtPrinterPayload,
  type IsbtTemplate,
} from '@/api/labelPrintingApi'
import { useLabelPrintingMutations, useLabelPrintingOptions } from '@/hooks/useLabelPrinting'
import {
  MESSAGES,
  canTestPrint,
  isbtAllowedChoices,
  isbtPendingCount,
  isbtTestPrintOptions,
  pendingTemplatesNote,
  pruneIsbtAllowed,
  queueNameCount,
  queueNameTooLong,
  validateIsbtPrinter,
  type IsbtPrinterDraft,
  type IsbtPrinterErrors,
} from '@/lib/labelPrinting'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  AllowedTemplatesField,
  Field,
  LocationInput,
  StatusBanner,
  SwitchField,
  activeNote,
  signatureNote,
} from './shared'

function draftOf(p: IsbtPrinter | undefined, firstSize: string): IsbtPrinterDraft {
  return p
    ? {
        displayName: p.displayName,
        queueName: p.queueName,
        loadedStock: p.loadedStock,
        // Retired links included: they stay on the printer (requirement 9).
        allowedTemplateIds: p.allowedTemplates.map(t => t.id),
        location: p.location ?? '',
        signatureRequired: p.signatureRequired,
        active: p.active,
      }
    : {
        displayName: '',
        queueName: '',
        loadedStock: firstSize,
        allowedTemplateIds: [],
        location: '',
        signatureRequired: true,
        active: true,
      }
}

const errorText = (e: unknown, fallback: string) => (e instanceof Error ? e.message : fallback)

type TestResult = { tone: 'ok' | 'error'; text: string }

/** Add or edit an ISBT printer, and send a test print from it, saved or not. */
export function IsbtPrinterDialog({
  printer,
  templates,
  locations,
  onClose,
  onDone,
}: {
  printer?: IsbtPrinter
  templates: IsbtTemplate[]
  locations: string[]
  onClose: () => void
  onDone: (message: string) => void
}) {
  const options = useLabelPrintingOptions()
  const sizes = options.data?.isbtSizes ?? []
  const { createIsbtPrinter, updateIsbtPrinter, testPrint } = useLabelPrintingMutations()

  const [draft, setDraft] = useState(() => draftOf(printer, sizes[0] ?? ''))
  const [touched, setTouched] = useState(false)
  const [serverError, setServerError] = useState<string | null>(null)
  // '' means the first template the picker offers.
  const [testTemplateId, setTestTemplateId] = useState('')
  const [testResult, setTestResult] = useState<TestResult | null>(null)

  // Options may arrive after a new draft was made: take the first size then.
  // The select offers no empty choice, so '' means the user has not picked.
  const firstSize = sizes[0]
  useEffect(() => {
    if (!printer && firstSize !== undefined) {
      setDraft(d => (d.loadedStock === '' ? { ...d, loadedStock: firstSize } : d))
    }
  }, [printer, firstSize])

  const linked = printer?.allowedTemplates ?? []
  const choices = isbtAllowedChoices(templates, draft.loadedStock, linked)
  const pendingNote = pendingTemplatesNote(isbtPendingCount(templates, draft.loadedStock))
  const testOptions = isbtTestPrintOptions(templates, draft.loadedStock)
  const testTemplate = testTemplateId || testOptions[0]?.id || ''

  const allErrors = validateIsbtPrinter(draft, choices)
  const tooLong = queueNameTooLong(draft.queueName)
  // Shown after a submit attempt; an over-long queue name shows as it is typed.
  const errors: IsbtPrinterErrors = touched ? allErrors : tooLong ? { queueName: allErrors.queueName } : {}
  const saving = createIsbtPrinter.isPending || updateIsbtPrinter.isPending

  const set = <K extends keyof IsbtPrinterDraft>(key: K, value: IsbtPrinterDraft[K]) =>
    setDraft(d => ({ ...d, [key]: value }))

  // A new size drops templates of the old one and resets the test pick (requirement 15).
  function changeStock(stock: string) {
    setDraft(d => ({
      ...d,
      loadedStock: stock,
      allowedTemplateIds: pruneIsbtAllowed(d.allowedTemplateIds, [...templates, ...linked], stock),
    }))
    setTestTemplateId('')
    setTestResult(null)
  }

  // A result belongs to the queue and template it was sent with.
  function changeQueueName(queueName: string) {
    set('queueName', queueName)
    setTestResult(null)
  }

  function changeTestTemplate(id: string) {
    setTestTemplateId(id)
    setTestResult(null)
  }

  function submit(e: FormEvent) {
    e.preventDefault()
    setTouched(true)
    if (Object.keys(allErrors).length > 0) return
    setServerError(null)
    const name = draft.displayName.trim()
    const payload: IsbtPrinterPayload = {
      displayName: name,
      queueName: draft.queueName.trim(),
      loadedStock: draft.loadedStock,
      location: draft.location.trim() || null,
      signatureRequired: draft.signatureRequired,
      allowedTemplateIds: draft.allowedTemplateIds,
    }
    const done = {
      onSuccess: () => onDone(`Saved “${name}”.`),
      onError: (err: unknown) => setServerError(errorText(err, 'Could not save the printer.')),
    }
    if (printer) updateIsbtPrinter.mutate({ id: printer.id, payload: { ...payload, active: draft.active } }, done)
    else createIsbtPrinter.mutate(payload, done)
  }

  // Needs only a name and a valid queue name: the printer need not be saved yet.
  function sendTestPrint() {
    if (!testTemplate) return
    setTestResult(null)
    testPrint.mutate(
      {
        templateId: testTemplate,
        queueName: draft.queueName.trim(),
        loadedStock: draft.loadedStock,
        ...(printer ? { printerId: printer.id } : {}),
      },
      {
        onSuccess: () => setTestResult({ tone: 'ok', text: MESSAGES.testPrintSent }),
        onError: err =>
          setTestResult({
            tone: 'error',
            text:
              err instanceof LabelPrintingApiError && err.status === 503
                ? MESSAGES.testPrintUnavailable
                : errorText(err, 'Could not send the test label.'),
          }),
      },
    )
  }

  const testReady = canTestPrint(draft)

  return (
    <Dialog open onOpenChange={o => !o && onClose()}>
      <DialogContent className='max-h-[90vh] overflow-y-auto sm:max-w-xl'>
        <DialogHeader>
          <DialogTitle>{printer ? 'Edit ISBT printer' : 'New ISBT printer'}</DialogTitle>
          <DialogDescription>A printer set up on the print computer, reached by its queue name.</DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} noValidate className='flex flex-col gap-4'>
          <Field
            id='isbt-printer-name'
            label='Display name'
            help='What the user picks when printing.'
            error={errors.displayName}
          >
            <Input
              id='isbt-printer-name'
              value={draft.displayName}
              autoFocus
              autoComplete='off'
              aria-invalid={!!errors.displayName}
              aria-describedby={errors.displayName ? 'isbt-printer-name-error' : undefined}
              onChange={e => set('displayName', e.target.value)}
            />
          </Field>

          <Field
            id='isbt-printer-queue'
            label='Queue name'
            help='The printer name exactly as set up on the print computer.'
            error={errors.queueName}
            aside={
              <span
                className={cn(
                  'text-xs tabular-nums',
                  tooLong ? 'font-semibold text-destructive' : 'text-muted-foreground',
                )}
              >
                {queueNameCount(draft.queueName)}
              </span>
            }
          >
            <Input
              id='isbt-printer-queue'
              value={draft.queueName}
              autoComplete='off'
              spellCheck={false}
              aria-invalid={!!errors.queueName}
              aria-describedby={errors.queueName ? 'isbt-printer-queue-error' : undefined}
              onChange={e => changeQueueName(e.target.value)}
            />
          </Field>

          <Field id='isbt-printer-stock' label='Loaded label size' help='Changing it clears templates of another size.'>
            <Select value={draft.loadedStock} onValueChange={changeStock}>
              <SelectTrigger id='isbt-printer-stock'>
                <SelectValue placeholder='Select a size' />
              </SelectTrigger>
              <SelectContent>
                {sizes.map(s => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <AllowedTemplatesField
            legend={`Allowed templates · ${draft.loadedStock}`}
            choices={choices}
            selected={draft.allowedTemplateIds}
            onChange={ids => set('allowedTemplateIds', ids)}
            emptyText='No approved templates of this size yet.'
            note={pendingNote}
            error={errors.allowed}
          />

          <div className='flex flex-col gap-2 rounded-lg border p-3'>
            <Field
              id='isbt-printer-test'
              label='Test print with'
              help='Any active template of this size, approved or not: a test print is how a template gets approved. The label uses made-up values, never patient data.'
            >
              <Select value={testTemplate} onValueChange={changeTestTemplate} disabled={testOptions.length === 0}>
                <SelectTrigger id='isbt-printer-test'>
                  <SelectValue placeholder='No active template of this size' />
                </SelectTrigger>
                <SelectContent>
                  {testOptions.map(o => (
                    <SelectItem key={o.id} value={o.id}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <div className='flex flex-wrap items-center gap-3'>
              <Button
                type='button'
                variant='outline'
                disabled={!testReady || !testTemplate || testPrint.isPending}
                onClick={sendTestPrint}
              >
                <Printer className='mr-2 h-4 w-4' />
                {testPrint.isPending ? 'Sending…' : 'Test print'}
              </Button>
              {!testReady && (
                <span className='text-xs text-muted-foreground'>
                  Enter a display name and a valid queue name first.
                </span>
              )}
            </div>
            {testResult && <StatusBanner tone={testResult.tone}>{testResult.text}</StatusBanner>}
          </div>

          <Field id='isbt-printer-location' label='Department / location'>
            <LocationInput
              id='isbt-printer-location'
              value={draft.location}
              onChange={v => set('location', v)}
              suggestions={locations}
            />
          </Field>

          <SwitchField
            id='isbt-printer-signature'
            label='Signature required to print'
            checked={draft.signatureRequired}
            onCheckedChange={v => set('signatureRequired', v)}
            note={signatureNote(draft.signatureRequired, true)}
          />

          {printer && (
            <SwitchField
              id='isbt-printer-active'
              label='Active'
              checked={draft.active}
              onCheckedChange={v => set('active', v)}
              note={activeNote(draft.active)}
            />
          )}

          {serverError && (
            <p role='alert' className='text-sm text-destructive'>
              {serverError}
            </p>
          )}

          <DialogFooter>
            <Button type='button' variant='outline' onClick={onClose}>
              Cancel
            </Button>
            <Button type='submit' disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
